/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : KDN V1.0
 * 설명 : 사용자페이지 헤더
 * 버전 : 1.0
 * 파일명 : Recent_3_Year.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정일 : 2025-09-16, 강동형 : 저작권 추가, 그래프 최댓값 항상 여유남도록 변경
 * ================================================================ */

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { Box, styled, Typography } from '@mui/material';
import { getRecentInterestedData } from 'src/api/Admin';
import { useEffect, useState } from 'react';

const ChartWrapper = styled('div')(({theme})=>({
  display:'flex',
  flexDirection:'column',

  position:'relative',
  height:'calc(100vh - 202px)',
}))

const LegendWrapper = styled('div')(({theme})=>({
  position : 'absolute',
  top : 60,
  right:70,
  zIndex:1
}))

const legendPayload = [
  { value: '2023', color: '#189187' },
  { value: '2024', color: '#1CC095' },
  { value: '2025', color: '#31F2BD' },
];

const CustomLegend = ({ payload }: { payload: typeof legendPayload }) => (
  <Box display="flex" gap={2}>
    {payload.map((entry, index) => (
      <Box key={index} display="flex" alignItems="center" gap={1}>
        <Box width={12} height={12} bgcolor={entry.color} borderRadius={'100%'} />
        <Typography variant="body2" color="text.secondary" fontSize={13}>
          {entry.value}
        </Typography>
      </Box>
    ))}
  </Box>
); 

const CustomBarLabel =
    (label: string, color: string) =>
  ({ x, y, width, height }: any) =>
    (
      <text
        x={x + width / 2}
        y={y + height + 16} // 막대 아래에 표시
        textAnchor="middle"
        fill={color}
        fontSize={14}
      >
        {label}
      </text>
    );

const CustomTick = ({
  x,
  y,
  payload,
}: {
  x: number
  y: number
  payload: { value: string }
}) => {
  const tickWidth = 410 / 3 // ✅ 핵심 계산
  return (
    <g transform={`translate(${x}, ${y})`}>
      <foreignObject x={-tickWidth/2} y={30} width={tickWidth} height={40}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px solid #96B9C4',
            padding: '12px',
            width: '100%',
            fontSize: '16px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            borderRadius: '5px',
            maxHeight: '40px',
            backgroundColor: 'white',
          }}
        >
          {payload.value}
        </div>
      </foreignObject>
    </g>
  );
};

// const groupedData = [
//     { category: "참여", "2023": 0, "2024": 7, "2025": 8 },
//     { category: "관심", "2023": 0, "2024": 2, "2025": 3 },
//     { category: "지원준비", "2023": 0, "2024": 1, "2025": 2 },
//     { category: "지원완료", "2023": 0, "2024": 1, "2025": 1 },
//     { category: "임관", "2023": 0, "2024": 1, "2025": 1 },
// ];

const Recent_3_Year = () => {
    const [data,setData] = useState([])
    useEffect(()=>{
        const fetchData = async () => {
            const response = await getRecentInterestedData();
            setData(response.data);
        };
        
        fetchData();
    },[])

    // 10^k 단위로 '항상' 다음 단계로 올리는 함수
    const niceCeil = (n: number) => {
      if (!isFinite(n) || n <= 0) return 10;           // 데이터가 없을 때 기본값
      const pow = Math.pow(10, Math.floor(Math.log10(n)));
      let top = Math.ceil(n / pow) * pow;              // 1200 -> 2000, 5800 -> 6000
      if (top === n) top += pow;                       // 2000 -> 3000 (항상 데이터보다 큼)
      return top;
    };

    const years = ['CurrentYear','LastYear','LastYear2'] as const
    const numericValues = data.flatMap(item => years.map(y => Number(item[y] ?? 0)));
    const dataMax = Math.max(0, ...numericValues);

    const max = niceCeil(dataMax);
    
    const step = max / 4;
    const ticks = Array.from({ length: 5 }, (_, i) => Math.round(i * step));

    return (
        <ChartWrapper>
            {/* ✅ 차트 위에 겹치는 Legend */}
            <LegendWrapper>
                <CustomLegend payload={legendPayload} />
            </LegendWrapper>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} barGap={10} margin={{top:50,right:0,bottom:90,left:45,}}>
                <CartesianGrid vertical={false}/>
                <XAxis dataKey="category" axisLine={false} tickLine={false} tick={(props) => <CustomTick {...props}/>}/>
                <YAxis domain={[0, max]} ticks={ticks} tickLine={false} axisLine={{stroke:'#CCCCCC'}} yAxisId="left" orientation="left"
                    tick={({ x, y, payload }) =>
                        payload.value === 0 ? (
                        <g /> // ✅ 빈 요소로 대체
                        ) : (
                        <text
                            x={x - 10}
                            y={y + 4}
                            textAnchor="end"
                            fill="#8A8A8A"
                            fontSize="14px"
                        >
                            {payload.value}
                        </text>
                        )
                    }/>
                <YAxis yAxisId="right" orientation="right" tick={false} axisLine={{stroke:'#CCCCCC'}}/>
                    <Tooltip />
                <Bar dataKey="LastYear2" fill="#189187" radius={[10, 10, 0, 0]} yAxisId="left">
                    <LabelList content={CustomBarLabel("2023", "#8A8A8A")} />
                </Bar>
                <Bar dataKey="LastYear" fill="#1CC095" radius={[10, 10, 0, 0]} yAxisId="left">
                    <LabelList content={CustomBarLabel("2024", "#8A8A8A")} />
                </Bar>
                <Bar dataKey="CurrentYear" fill="#31F2BD" radius={[10, 10, 0, 0]} yAxisId="left">
                    <LabelList content={CustomBarLabel("2025", "#8A8A8A")} />
                </Bar>
                </BarChart> 
            </ResponsiveContainer>
        </ChartWrapper>
    )
}

export default Recent_3_Year